import { IUserNotification } from './user-notification.model';

/**
 * Compares two notifications to determine if any meaningful property has changed
 * @param current The current notification
 * @param updated The updated notification
 * @returns true if the notification has changed, false otherwise
 */
export function hasNotificationChanged(
  current: IUserNotification,
  updated: IUserNotification
): boolean {
  // IDs must match to compare the same notification
  if (current.id !== updated.id) {
    throw new Error('Cannot compare notifications with different IDs');
  }

  // Check key fields that indicate changes
  return (
    current.isRead !== updated.isRead ||
    current.status !== updated.status ||
    current.title !== updated.title ||
    current.body !== updated.body ||
    current.isActive !== updated.isActive ||
    current.updatedTimestamp !== updated.updatedTimestamp ||
    current.updatedBy !== updated.updatedBy ||
    current.sendToTeams !== updated.sendToTeams
  );
}

/**
 * Finds a notification by ID in a list
 * @param notifications Array of notifications
 * @param notificationId The notification ID to find
 * @returns The found notification or undefined
 */
export function findNotificationById(
  notifications: IUserNotification[],
  notificationId: string
): IUserNotification | undefined {
  return notifications.find(n => n.id === notificationId);
}

/**
 * Merges or adds a notification to a list, maintaining uniqueness by ID
 * @param notifications Current notification list
 * @param notification Notification to merge or add
 * @returns Updated notification list
 */
export function mergeNotification(
  notifications: IUserNotification[],
  notification: IUserNotification
): IUserNotification[] {
  const existingIndex = notifications.findIndex(n => n.id === notification.id);

  if (existingIndex >= 0) {
    // Update existing notification
    const updated = [...notifications];
    updated[existingIndex] = notification;
    return updated;
  } else {
    // Add new notification at the beginning
    return [notification, ...notifications];
  }
}

export const normalizeTaskMessage = (input: string): string => {
  let cleaned = input;

  // Remove markdown links like: [TEXT](URL)
  cleaned = cleaned.replace(/\[([^\]]+)\]\([^)]+\)/g, "$1");

  
  // --- Edge Case: Replace [object Object] with "unknown"
  cleaned = cleaned.replace(/\[object Object\]/gi, "unknown");

  // CASE A: CREATION — remove task code/description between "The task" and "for"
  // Handles both markdown and plain quoted/unquoted task labels
  if (/has been created and assigned/i.test(cleaned)) {
    cleaned = cleaned.replace(
      /The task\s+['"]?.*?['"]?\s+for/i,
      "The task for"
    );
    return cleaned;
  }

  // CASE B: Completed + next task
  if (/has been completed and the next task/i.test(cleaned)) {
    if (!cleaned.trim().startsWith("The task")) {
      cleaned = cleaned.replace(/^[^"]*?has been/i, "This task has been");
    }
    return cleaned;
  }

  // CASE C: Completed by user
  if (/has been completed by/i.test(cleaned)) {
    cleaned = cleaned.replace(/^\s*"?\[?.*?\]? has been/i, "This task has been");
    return cleaned;
  }

  // -------- CASE D: Rejected by user --------
  if (/has been rejected by/i.test(cleaned)) {
    // Remove task name before "has been rejected"
    cleaned = cleaned.replace(/^The task\s+['"]?.*?['"]?\s+has been/i, "This task has been");
    return cleaned;
  }

  return cleaned;
}